import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
} from "react";
import { arrestDates } from "./ArrestDates";
import { exampleCases } from "./ExampleCases";

type SectionPhase = "idle" | "circle-in" | "line-in" | "typing" | "done";

const CIRCLE_IN_MS = 400;
const LINE_IN_MS = 600;
const CHAR_MS = 22.5; // ms per character typed
const HEAD_START_CHARS = Math.round(200 / CHAR_MS); // date types before description
const STAGGER_MS = 1000; // delay before the second case starts

const SHOW_DATES_ABOVE_DESCRIPTION = false;

type Char = { char: string; highlighted: boolean };

const buildChars = (
  example: typeof exampleCases[0],
): { dateChars: Char[]; descChars: Char[] } => {
  const simplifiedDate = new Date(example.arrest_date).toLocaleDateString(
    "en-US",
    { month: "long", day: "numeric", year: "numeric" },
  );

  const dateChars: Char[] = simplifiedDate
    .toUpperCase()
    .split("")
    .map((char) => ({ char, highlighted: false }));

  if (!example.description.includes(example.bolded_part)) {
    throw new Error(
      `Description for ${example.name} does not contain the bolded_part!`,
    );
  }

  const descriptionParts = example.description.split(example.bolded_part);

  const parts: Array<{ text: string; highlighted: boolean }> = [
    { text: example.name.toUpperCase(), highlighted: true },
    { text: " FROM ", highlighted: false },
    { text: example.origin_country.toUpperCase(), highlighted: true },
    { text: ": ", highlighted: false },
    { text: descriptionParts[0].toUpperCase(), highlighted: false },
    { text: example.bolded_part.toUpperCase(), highlighted: true },
    { text: descriptionParts[1].toUpperCase(), highlighted: false },
  ];

  const descChars = parts.flatMap(({ text, highlighted }) =>
    text.split("").map((char) => ({ char, highlighted })),
  );

  return { dateChars, descChars };
};

const calculateDaysBetweenDates = (start: string, end: string) => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  return Math.floor(
    (endDate.getTime() - startDate.getTime()) / (1000 * 3600 * 24),
  );
};

// One-shot phase machine for a single case.
// Call start() to begin. Stays in "done" once text is fully revealed.
function useCaseAnimation(totalChars: number) {
  const [phase, setPhase] = useState<SectionPhase>("idle");
  const [revealedChars, setRevealedChars] = useState(0);

  const start = () => setPhase("circle-in");

  useEffect(() => {
    if (phase === "idle" || phase === "done") return;
    let timeout: ReturnType<typeof setTimeout>;

    switch (phase) {
      case "circle-in":
        timeout = setTimeout(() => setPhase("line-in"), CIRCLE_IN_MS);
        break;
      case "line-in":
        timeout = setTimeout(() => {
          setRevealedChars(0);
          setPhase("typing");
        }, LINE_IN_MS);
        break;
      case "typing":
        if (revealedChars >= HEAD_START_CHARS + totalChars) {
          setPhase("done");
        } else {
          timeout = setTimeout(
            () => setRevealedChars((prev) => prev + 1),
            CHAR_MS,
          );
        }
        break;
    }

    return () => clearTimeout(timeout);
  }, [phase, revealedChars, totalChars]);

  return { phase, revealedChars, start };
}

interface Props {
  section: string;
}

/**
 * Reads CSS custom properties from the section-animation container so JS
 * layout calculations stay in sync with whatever SCSS breakpoint is active.
 */
const useSectionDimensions = (ref: React.RefObject<HTMLElement>) => {
  const [dims, setDims] = useState({
    circleSize: 45,
    lineLength: 14,
    containerHeight: 80,
  });

  useLayoutEffect(() => {
    if (!ref.current) return;
    const s = getComputedStyle(ref.current);
    const n = (prop: string) => Number(s.getPropertyValue(prop).trim());
    setDims({
      circleSize: n("--section-circle-size"),
      lineLength: n("--section-line-length"),
      containerHeight: n("--section-container-height"),
    });
  }, [ref]);

  useEffect(() => {
    const read = () => {
      if (!ref.current) return;
      const s = getComputedStyle(ref.current);
      const n = (prop: string) => Number(s.getPropertyValue(prop).trim());
      setDims({
        circleSize: n("--section-circle-size"),
        lineLength: n("--section-line-length"),
        containerHeight: n("--section-container-height"),
      });
    };
    window.addEventListener("resize", read);
    return () => window.removeEventListener("resize", read);
  }, [ref]);

  return dims;
};

export const SectionAnimation: React.FC<Props> = ({ section }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { circleSize, lineLength, containerHeight } =
    useSectionDimensions(containerRef);

  const timelineStart = "10/14/2025";
  const timelineEnd = "03/14/2026";
  const totalDays = calculateDaysBetweenDates(timelineStart, timelineEnd);
  const getHorizontalPosition = (days: number) => (days / totalDays) * 100;

  const sectionCases = useMemo(
    () => exampleCases.filter((c) => c.section === section).slice(0, 2),
    [section],
  );

  const sectionNumber = parseInt(section.split(" ")[1], 10);

  // containerHeight is read from CSS custom property --section-container-height via useSectionDimensions
  const axisPosition =
    sectionCases.length === 2 ? containerHeight / 2 : containerHeight / 2 - 20;
  // lineLength is read from CSS custom property --section-line-length via useSectionDimensions

  const allCaseData = useMemo(
    () => sectionCases.map(buildChars),
    [sectionCases],
  );

  // Always call both hooks — conditionally ignore case1 when there's only 1 case
  const case0 = useCaseAnimation(allCaseData[0]?.descChars.length ?? 0);
  const case1 = useCaseAnimation(allCaseData[1]?.descChars.length ?? 0);

  const caseAnimations = [case0, case1];

  // Trigger on scroll into view — case 0 immediately, case 1 after STAGGER_MS
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          case0.start();
          if (sectionCases.length > 1) {
            setTimeout(() => case1.start(), STAGGER_MS);
          }
          observer.disconnect();
        }
      },
      { threshold: 0.35 },
    );
    observer.observe(el);
    return () => observer.disconnect();
    // case0.start / case1.start are stable (defined in the hook, no deps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionCases.length]);

  const getCaseLayout = (caseIdx: number) => {
    const c = sectionCases[caseIdx];
    const daysIn = calculateDaysBetweenDates(timelineStart, c.arrest_date);
    const hp = getHorizontalPosition(daysIn);
    const lineLeft = `calc(${hp}vw + ${circleSize / 2}px)`;
    const descStyle: React.CSSProperties =
      hp > 50
        ? {
            right: `calc(${100 - hp}vw - ${circleSize / 2}px)`,
            width: "600px",
          }
        : { left: `calc(${hp}vw + ${circleSize / 2}px)` };
    return {
      hp,
      lineLeft,
      descStyle,
      colorClass: `case-color-${(caseIdx - 1 + sectionNumber) % 3}`, // Vary color based on both case index and section number to avoid repetition
    };
  };

  return (
    <div
      ref={containerRef}
      className="section-animation"
      style={{ position: "relative", width: "100%" }}
    >
      {/* Timeline axis */}
      <div
        className="has-background-white"
        style={{
          position: "absolute",
          top: `${axisPosition}vh`,
          width: "100%",
          height: "1px",
        }}
      />

      {/* Axis tick labels */}
      {["Nov '25", "Jan '26", "Mar '26"].map((label, i) => (
        <div
          key={i}
          className="eyebrow axis-labels has-text-centered"
          style={{
            position: "absolute",
            top: `calc(${axisPosition}vh + ${circleSize / 2}px + 10px)`,
            left: `calc(${i * 40 + 10}vw - 25px)`,
          }}
        >
          {label}
        </div>
      ))}

      {/* Background arrest circles */}
      {arrestDates.map((arrest, i) => {
        const pos = getHorizontalPosition(arrest.days_in);
        return (
          <div
            key={i}
            className="has-background-grey-light"
            style={{
              position: "absolute",
              left: `calc(${pos}vw)`,
              top: `calc(${axisPosition}vh - ${circleSize / 2}px)`,
              width: `${circleSize}px`,
              height: `${circleSize}px`,
              zIndex: 10,
              opacity: ((arrest.number_of_arrests - 3) / 25) * 0.9,
              borderRadius: "100%",
            }}
          />
        );
      })}

      {/* Section example cases */}
      {sectionCases.map((_, caseIdx) => {
        const { hp, lineLeft, descStyle, colorClass } = getCaseLayout(caseIdx);
        const { dateChars, descChars } = allCaseData[caseIdx];
        const { phase, revealedChars } = caseAnimations[caseIdx];

        const goesUp = sectionCases.length === 2 && caseIdx === 0;

        const lineStyle: React.CSSProperties = goesUp
          ? {
              position: "absolute",
              top: `calc(${axisPosition}vh - ${
                circleSize / 2
              }px - 10px - ${lineLength}vh)`,
              left: lineLeft,
              height: `${lineLength}vh`,
              width: "1px",
              opacity: 0.5,
              transformOrigin: "bottom",
            }
          : {
              position: "absolute",
              top: `calc(${axisPosition}vh + ${circleSize / 2}px + 10px)`,
              left: lineLeft,
              height: `${lineLength}vh`,
              width: "1px",
              opacity: 0.5,
              transformOrigin: "top",
            };

        const datePositionTop = goesUp
          ? `calc(${axisPosition}vh - ${
              circleSize / 2
            }px - 10px - ${lineLength}vh - 80px)`
          : `calc(${axisPosition + lineLength}vh + ${circleSize / 2}px + 20px)`;

        const descPositionTop = goesUp
          ? `calc(${axisPosition}vh - ${
              circleSize / 2
            }px - 10px - ${lineLength}vh - 50px)`
          : `calc(${axisPosition + lineLength}vh + ${circleSize / 2}px + ${
              SHOW_DATES_ABOVE_DESCRIPTION ? 45 : 25
            }px)`;

        return (
          <div key={caseIdx} className={`example-case ${colorClass}`}>
            {/* Vertical connecting line */}
            <div
              className={`has-background-white example-case-line phase-${phase}`}
              style={lineStyle}
            />

            {/* Date eyebrow — types out first, ~200ms before description */}
            {SHOW_DATES_ABOVE_DESCRIPTION && (
              <div
                className={`example-case-date eyebrow phase-${phase}`}
                style={{
                  position: "absolute",
                  top: datePositionTop,
                  ...descStyle,
                }}
              >
                {dateChars.map((c, i) => (
                  <span key={i} style={{ opacity: i < revealedChars ? 1 : 0 }}>
                    {c.char}
                  </span>
                ))}
              </div>
            )}

            {/* Case description — starts typing ~200ms after the date */}
            <div
              className={`example-case-description eyebrow phase-${phase}`}
              style={{
                position: "absolute",
                top: descPositionTop,
                maxWidth: "600px",
                ...descStyle,
              }}
            >
              {descChars.map((c, i) => (
                <span
                  key={i}
                  className={c.highlighted ? "example-case-data" : undefined}
                  style={{
                    opacity: i < revealedChars - HEAD_START_CHARS ? 1 : 0,
                  }}
                >
                  {c.char}
                </span>
              ))}
            </div>

            <div
              className={`example-case-circle phase-${phase}`}
              style={{
                position: "absolute",
                left: `calc(${hp}vw)`,
                top: `calc(${axisPosition}vh - ${circleSize / 2}px)`,
                width: `${circleSize}px`,
                height: `${circleSize}px`,
                zIndex: 20,
                borderRadius: "100%",
              }}
            />
          </div>
        );
      })}
    </div>
  );
};
